/** @jsx jsx */
import { jsx, Box, Flex } from 'theme-ui';
import React from 'react';
import { capitalize } from '../utils/string';
import Button from '../components/Button';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import { Link } from '../components';
import { useMemo } from 'react';
import { UAParser } from 'ua-parser-js';
import { DELIVERY_START_PRICE } from '../state/catalog';

export const STEP = {
  START: 0,
  BASIC_INFO: 1,
  DELIVERY_INSTRUCTIONS: 2,
  READY: 3,
  NOT_FOUND: 5,
  INVALID_PARAMETERS: 6,
  ARE_YOU_SURE_TO_SERVICE_POINT: 7,
  THANKS_TO_SERVICE_POINT: 8,
  ARE_YOU_SURE_NOT_RECEIVE: 10,
  THANKS_NOT_RECEIVE: 11,
  WOLT_PAYMENT: 12,
  WOLT_PAYMENT_OK: 13,
  AGREED: 14,
  EDIT: 15,
  MAP: 16,
  CHOOSE_SERVICE_POINT: 17,
  DELIVERY_UNATTENDED: 18,
  BUY_CONTACT_DELIVERY: 19,
  CONFIRM_HOME_DELIVERY: 20,
  CONTACT_DELIVERY_OK: 21,
};

export const formatAddress = (address) => {
  const postalCode = /\d{5} /.exec(address || '');
  if (!postalCode) {
    return address || '';
  }

  return (
    <Box>
      <Box as="span" sx={{ display: 'block' }}>
        {address.substr(0, postalCode.index).trim()}
      </Box>
      <Box as="span" sx={{ display: 'block' }}>
        {capitalize(address.substr(postalCode.index).trim())}
      </Box>
    </Box>
  );
};

export const BackButton = ({ goBack }) => {
  const { t: translate } = useTranslation();
  return (
    <Box>
      <Button onClick={goBack} variant="plain" sx={{ color: 'primary' }}>
        {translate('delivery.agree.back')}
      </Button>
    </Box>
  );
};

export const BlueInfoBox = ({ deliveryAddress, day, time, shipment, shipmentStatus }) => {
  const { t: translate, i18n } = useTranslation();
  const address = formatAddress(deliveryAddress);
  const language = i18n.language;
  const showWaitingForSenderInfo = (shipmentStatus || shipment?.shipmentStatus) === '02';
  const personalHandover = !!shipment?.additionalServices?.some((service) => service.additionalServiceCode === 'HL');

  const [deliveryAccurateStart, deliveryAccurateEnd] = useMemo(() => {
    return [
      moment(shipment?.estimatedDeliveryStartTime).tz('Europe/Helsinki').locale(language),
      moment(shipment?.estimatedDeliveryEndTime).tz('Europe/Helsinki').locale(language),
    ];
  }, [shipment, language]);

  const accurateDateText = useMemo(() => {
    if (!deliveryAccurateStart) {
      return null;
    }

    return moment().isSame(deliveryAccurateStart, 'day')
      ? translate('delivery.agree.today')
      : moment(deliveryAccurateStart).format('DD.MM.YYYY');
  }, [deliveryAccurateStart]);

  return (
    <Box sx={{ bg: 'blueLighter', p: 3, borderRadius: 1, color: 'black' }}>
      {showWaitingForSenderInfo && (
        <Box sx={{ mb: 3 }}>
          <p sx={{ mt: 0, mb: 0, whiteSpace: 'pre-line' }}>
            {translate('delivery.agree.waitingForSender', { shipmentNumber: shipment?.shipmentNumber })}
          </p>
          <p sx={{ mt: 3, mb: 0 }}>
            {translate(
              personalHandover
                ? 'delivery.agree.waitingForSenderPersonalHandover'
                : 'delivery.agree.waitingForSenderActions'
            )}
          </p>
        </Box>
      )}
      {address}
      {shipment?.estimatedDeliveryStartTime ? (
        <>
          <br />
          <br />
          <span sx={{ fontWeight: 'medium' }}>
            {accurateDateText} {translate('delivery.agree.atTime')} {deliveryAccurateStart.format('HH:mm')} -{' '}
            {deliveryAccurateEnd.format('HH:mm')}.
          </span>
        </>
      ) : time ? (
        <>
          <br />
          <br />
          <span sx={{ fontWeight: 'medium' }}>
            {moment(day).format('dddd D.M.YYYY')}, {time.label}
          </span>
        </>
      ) : null}
    </Box>
  );
};

const agreedDeliveryInfoBoxTitleSx = {
  fontSize: 1,
  fontWeight: 'medium',
  mb: 1,
};

const AgreedDeliveryInfoBoxSection = ({ title, children, sx }) => (
  <Box sx={{ mb: 3, ...sx }}>
    <Box sx={agreedDeliveryInfoBoxTitleSx}>{title}</Box>
    <Box>{children}</Box>
  </Box>
);

const formatHours = (timestamp) => {
  const date = moment(timestamp).tz('Europe/Helsinki');
  return date.format(date.minutes() === 0 ? 'H' : 'H:mm');
};

const hasAdditionalService = (shipment, code) =>
  !!shipment?.additionalServices?.some((service) => service.additionalServiceCode === code);

export const isPersonalHandover = (shipment) => hasAdditionalService(shipment, 'HL');

export const isContactDelivery = (shipment) => hasAdditionalService(shipment, 'KT');

export const getShipmentDestinationAddress = (shipment) => {
  const destinationFromReceiver = [
    shipment?.receiverStreetAddress,
    [shipment?.receiverPostalCode, shipment?.receiverCity].filter(Boolean).join(' '),
  ]
    .filter(Boolean)
    .join(' ');

  return destinationFromReceiver || shipment?.deliveryAddress || '';
};

const formatSelectedDeliveryStartTime = (day, time) => {
  if (!day || !time?.start) {
    return null;
  }

  return moment.tz(
    `${day} ${time.start}`,
    ['YYYY-MM-DD HH:mm:ss.SSS', 'YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD HH:mm'],
    'Europe/Helsinki'
  );
};

const formatAgreedDeliveryDateTime = ({ shipment, day, time, translate }) => {
  const deliveryStartTime = shipment?.estimatedDeliveryStartTime || shipment?.agreedDeliveryStartTime;
  const date = deliveryStartTime
    ? moment(deliveryStartTime).tz('Europe/Helsinki')
    : formatSelectedDeliveryStartTime(day, time);

  if (!date?.isValid()) {
    return null;
  }

  return translate('delivery.agree.agreedDeliveryInfoBox.deliveryDateTime', {
    date: date.format('DD.MM.'),
    time: date.format('HH:mm'),
  });
};

export function formatDeliveryDate(shipment) {
  const date = shipment?.estimatedDeliveryStartTime || shipment?.agreedDeliveryStartTime || shipment?.etaDate;

  return date ? moment(date).tz('Europe/Helsinki').format('D.M.') : null;
}

export const DEFAULT_DELIVERY_WINDOW = '8-18';

export function formatDeliveryWindow(shipment) {
  const [startTime, endTime] =
    shipment?.estimatedDeliveryStartTime && shipment?.estimatedDeliveryEndTime
      ? [shipment.estimatedDeliveryStartTime, shipment.estimatedDeliveryEndTime]
      : [shipment?.agreedDeliveryStartTime, shipment?.agreedDeliveryEndTime];

  if (!startTime || !endTime) {
    return DEFAULT_DELIVERY_WINDOW;
  }

  return `${formatHours(startTime)}-${formatHours(endTime)}`;
}

export const AgreedDeliveryInfoBox = ({ shipment, day, time }) => {
  const { t: translate } = useTranslation();
  const deliveryAddress = formatAddress(getShipmentDestinationAddress(shipment));
  const deliveryDateTime = formatAgreedDeliveryDateTime({ shipment, day, time, translate });

  return (
    <Box sx={{ bg: 'blueLighter', p: 3, borderRadius: 1, color: 'black' }}>
      <AgreedDeliveryInfoBoxSection title={translate('delivery.agree.agreedDeliveryInfoBox.deliveryAddressTitle')}>
        {deliveryAddress}
      </AgreedDeliveryInfoBoxSection>
      {deliveryDateTime && (
        <AgreedDeliveryInfoBoxSection
          title={translate('delivery.agree.agreedDeliveryInfoBox.deliveryTimeAndMethodTitle')}
        >
          {deliveryDateTime}
        </AgreedDeliveryInfoBoxSection>
      )}
      <AgreedDeliveryInfoBoxSection
        title={translate('delivery.agree.agreedDeliveryInfoBox.responsibilityTitle')}
        sx={{ mb: 0 }}
      >
        {translate('delivery.agree.agreedDeliveryInfoBox.responsibilityText')}
      </AgreedDeliveryInfoBoxSection>
    </Box>
  );
};

export const ConfirmHomeDeliveryBox = ({ shipment, onConfirm }) => {
  const { t: translate } = useTranslation();
  const deliveryDay = formatDeliveryDate(shipment);
  const deliveryTime = formatDeliveryWindow(shipment);

  const canConfirmHomeDelivery =
    shipment.isHomeDelivery &&
    shipment.deliveryIncluded &&
    !shipment.deliveryMethod &&
    shipment.deliveryTimeAgreeable &&
    +shipment.shipmentStatus > 8 &&
    +shipment.shipmentStatus < 40;

  const personalHandover = !!shipment?.additionalServices?.some((service) => service.additionalServiceCode === 'HL');

  const contactDelivery = !!shipment?.additionalServices?.some((service) => service.additionalServiceCode === 'KT');

  return (
    <Box sx={{ bg: 'blueLighter', p: 3, borderRadius: 1, color: 'black' }}>
      <p sx={{ mt: 0, mb: 3, fontWeight: 'medium' }}>{translate('delivery.agree.confirmHomeDelivery.title')}</p>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'max-content minmax(0, 1fr)',
          columnGap: 3,
          rowGap: 2,
          mb: 3,
        }}
      >
        <Box>{translate('delivery.agree.confirmHomeDelivery.estimatedDeliveryDay')}</Box>
        <Box sx={{ fontWeight: 'medium' }}>
          {translate('delivery.agree.confirmHomeDelivery.estimatedDeliveryDayValue', { day: deliveryDay })}
        </Box>
        <Box>{translate('delivery.agree.confirmHomeDelivery.time')}</Box>
        <Box sx={{ fontWeight: 'medium' }}>{deliveryTime}</Box>
      </Box>
      <p sx={{ mt: 0, mb: 0 }}>
        {personalHandover
          ? translate('delivery.agree.confirmHomeDelivery.personalHandover')
          : contactDelivery
            ? translate('delivery.agree.confirmHomeDelivery.contactDeliveryHandover', { time: deliveryTime })
            : translate('delivery.agree.confirmHomeDelivery.leaveAtDoor')}
      </p>
      {!personalHandover && (
        <p sx={{ mt: 0, mb: 3 }}>
          {translate(
            contactDelivery
              ? 'delivery.agree.confirmHomeDelivery.contactDeliveryLiabilityNote'
              : 'delivery.agree.confirmHomeDelivery.liabilityNote'
          )}
        </p>
      )}
      {canConfirmHomeDelivery && onConfirm && (
        <Flex sx={{ justifyContent: 'flex-end' }}>
          <Button type="button" onClick={onConfirm}>
            {translate('delivery.agree.confirmHomeDelivery.confirm')}
          </Button>
        </Flex>
      )}
    </Box>
  );
};

export const ButtonBox = ({ children }) => {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '200px',
        justifySelf: 'flex-end',
        gridGap: 2,
        flex: 'none',
        justifyContent: ['flex-end', null, 'flex-start'],
        mt: 3,
      }}
    >
      {children}
    </Box>
  );
};

export const date2dayString = (date) => moment.tz(date, 'Europe/Helsinki').format('YYYY-MM-DD');

export const setupMomentWeekdays = (language) => {
  if (language == 'fi') {
    moment.updateLocale('fi', {
      weekdays: ['Sunnuntaina', 'Maanantaina', 'Tiistaina', 'Keskiviikkona', 'Torstaina', 'Perjantaina', 'Lauantaina'],
    });
    moment.locale('fi');
  } else if (language == 'sv') {
    moment.locale('sv');
  } else if (language == 'en') {
    moment.locale('en');
  }
};

export const CalenderLink = ({ icsBase64, shipmentNumber }) => {
  const { t: translate } = useTranslation();
  if (!icsBase64) {
    return null;
  }
  return (
    <div sx={{ my: 3 }}>
      <a
        href={`data:text/calendar;base64;charset=utf-8,${icsBase64}`}
        download={translate('delivery.agree.filename', { shipmentNumber })}
        sx={{ textDecoration: 'underline' }}
      >
        {translate('delivery.agree.addCalendar')}
      </a>
    </div>
  );
};

const getAgreeDeliveryDeepLinkForApp = (shipmentNumber, token) => {
  const isTest = process.env.GATSBY_ACTIVE_ENV !== 'production';
  const prefix = isTest
    ? `https://mhpakettidev.page.link/?link=https://testwww.matkahuolto.fi/sovijakelu`
    : `https://paketit.matkahuolto.fi/?link=https://matkahuolto.fi/sovijakelu`;
  return `${prefix}/${shipmentNumber}/${token}`;
};

export const NavigationHeader = ({ shipmentNumber, token, goBack }) => {
  if (!shipmentNumber || !token) return null;
  const { t: translate } = useTranslation();
  return (
    <Flex sx={{ justifyContent: goBack ? 'space-between' : 'flex-end' }}>
      {goBack && (
        <Button onClick={goBack} variant="plain" sx={{ color: 'primary' }}>
          {translate('delivery.agree.back')}
        </Button>
      )}

      {isAndroid() && (
        <Button
          as={Link}
          to={{
            link: getAgreeDeliveryDeepLinkForApp(shipmentNumber, token),
            external: true,
          }}
        >
          {translate('delivery.agree.openApp')}
        </Button>
      )}
    </Flex>
  );
};

const formatTime = (time) => {
  // input "09:30:00.000"
  // output "9.30"
  const shortTime = time.substr(3, 2) == '00' ? time.substr(0, 2) : time.substr(0, 5).replace(':', '.');
  if (shortTime[0] == '0') {
    return shortTime.substr(1);
  } else {
    return shortTime;
  }
};

export const formatDeliverySlotTimeRange = ({ start, end }) => `${formatTime(start)} - ${formatTime(end)}`;

export const formatTimeRange = ({ start, end }, translate) => {
  const klo = translate('delivery.agree.clock');
  return `${klo} ${formatDeliverySlotTimeRange({ start, end })}`;
};

export const isAndroid = () => {
  const agent = UAParser();
  return agent?.os?.name == 'Android';
};
